import { Contact } from 'lib/api/contacts/types';

import { CURRENCIES, SGR_LOST_REASONS, SGR_STATES } from '../consts';

export interface Warranty {
  id: number;
  displayId: string;
  folioId?: number;
  type: string;
  status: (typeof SGR_STATES)[number]['value'];
  quotedAmount: number;
  quotedCurrency: (typeof CURRENCIES)[number]['value'];
  quoteDate: string;
  conversionRate?: number;
  fixedAmount?: number;
  fixingDate?: string;
  operationCostFixed?: number;
  operationCostVariable?: number;
  discountRateVariable?: number;
  discountRateFixed?: number;
  tenderId?: string;
  principalRut?: string;
  unitCode?: string;
  description?: string;
  claimReason?: string;
  lostReason?: (typeof SGR_LOST_REASONS)[number]['value'];
  lostDescription?: string;
  lostDate?: string;
  issuedDate?: string;
  claimDate?: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  businessIdentifier: string;
  documents?: Document[];
  premium?: number;
  comiteeId?: number;
  comitee?: CompanyComitee;
  claimedBy?: User;
  cancelReason?: string;
  cancelDate?: string;
  cancelledBy?: User;
  principal?: {
    name: string;
    identifier: string;
  };
}

export interface Document {
  id: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  type: string;
  subType?: string;
  folio: string;
  issuer: boolean;
  issuedDate: string;
  receiptDate?: string;
  confirmDate?: string;
  cancelDate?: string;
  amount: number;
  sgrWarrantyRequestId?: number;
  companyId?: number;
  paidAt?: string;
  nulledAt?: string;
  createdAt: string;
  fatherDocumentId?: string;
  pendingAccountId?: number;
  surplusId?: number;
  reversedAt?: string;
}

export interface Business {
  id: number;
  name: string;
  identifier: string;
  startDate: string;
  segment: string;
  publicMarketCode: string | null;
}

export interface BankAccount {
  id: number;
  bankName: string;
  accountNumber: string;
  type: string;
  selected: boolean;
  businessIdentifier: string;
  holderIdentifier: string;
  holderName: string;
  businessIsHolder: boolean;
  bankId: number;
  currency: string;
  email: string | null;
  powerDueDate: string | null;
}

export interface PayoutDistribution {
  bankAccount: BankAccount;
  amount: number;
}

export interface Company {
  id: number;
  secretId: string;
  name: string;
  active: boolean;
  tenant: boolean;
  taxIdentifier?: string;
}

export interface PayIn {
  id: string;
  amount: number;
  status: string;
  companyId: number;
  bankAccountDestinationId: number;
  bankAccountOriginId: number;
  createdAt: string;
}

export interface PayOut {
  id: number;
  source: string;
  sourceId: string;
  amount: number;
  companyId: number;
  executorId?: number;
  bankAccountOriginId: number;
  bankAccountDestinationId: number;
  status: string;
  createdAt: string;
  executedAt: string | null;
}

export interface BankAccount {
  id: number;
  bankName: string;
  bankId: number;
  holderName: string;
  holderIdentifier: string;
  accountNumber: string;
  type: string;
  businessIdentifier: string;
  currency: string;
  selected: boolean;
  business: Business;
  companyId: number;
  company: Company;
  email: string | null;
  powerDueDate: string | null;
}

export interface NotRecognizedConciliation {
  id: number;
  amount: number;
  companyId: number;
  payInId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  id: number;
  typeId: string;
  type?: ProductType;
  companyGiverId: number;
  creditLines: any[];
  requirements: any[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductType {
  id: string;
  name: string;
  description: string;
  products: Product[];
  createdAt: string;
  updatedAt: string;
  subProducts: SubProduct[];
}

export interface SubProduct {
  id: SubProductId;
  name: string;
  description: string;
  typeId: string;
  category: string;
  type?: ProductType;
  createdAt: string;
  updatedAt: string;
  SubProductCreditLineConcentration?: SubProductCreditLineConcentration[];
  FactoringRequest?: any[];
  documents?: any[];
  ConfirmingRequest?: any[];
}

export type SubProductId =
  | 'buy-order'
  | 'check-invoice'
  | 'contract'
  | 'electronic-invoice'
  | 'pending-account'
  | 'promissory-note'
  | 'check'
  | 'credit-request-cuota'
  | 'offer-seriousness'
  | 'faithful-compliance'
  | 'advance'
  | 'proper-performance'
  | 'faithful-compliance-performance'
  | 'warranty-premium';

export const SUBPRODUCT_LABELS: Record<SubProductId, string> = {
  'buy-order': 'Orden de compra',
  'check-invoice': 'Factura con flujo de cheques',
  contract: 'Contrato',
  'electronic-invoice': 'Factura Electrónica',
  'pending-account': 'Cuenta pendiente',
  'promissory-note': 'Pagaré',
  check: 'Cheque',
  'credit-request-cuota': 'Cuota de crédito',
  'offer-seriousness': 'Seriedad de oferta',
  'faithful-compliance': 'Fiel cumplimiento',
  advance: 'Anticipo',
  'proper-performance': 'Correcta ejecución',
  'faithful-compliance-performance': 'Fiel cumplimiento y correcta ejecución',
  'warranty-premium': 'Costo emisión certificado',
};

export interface DocumentPublication {
  id: number;
  type: 'Factura' | 'Contrato' | 'Pagare Adeudado';
  creditorIdentifier: string;
  companyId: number;
  documentId: string;
  serviceId: string;
  createdAt: string;
  updatedAt: string;
  unpublishedAt: string | null;
}

export interface BusinessSummary {
  id: number;
  businessIdentifier: string;
  business?: Business;
  companyId: number;
  company?: Company;
  email?: string | null;
  region?: string | null;
  province?: string | null;
  commune?: string | null;
  address?: string | null;
  economicSector?: string | null;
  economicSubSector?: string | null;
  economicActivity?: string | null;
  salesSegment?: string | null;
  uaf?: string | null;
  observations?: string | null;
}

export interface Address {
  id: number;
  region?: string | null;
  province?: string | null;
  commune?: string | null;
  address?: string | null;
  unitCode?: string | null;
  businessUnit?: BusinessUnit | null;
}

export interface BusinessUnit {
  unitCode: string;
  name: string;
  address?: Address | null;
  contacts: Contact[];
  createdAt: string;
  updatedAt: string;
  businessIdentifier: string;
  business: Business;
}
export type TenderItem = {
  id: string;
  correlativeId: number | null;
  productCode: string | null;
  categoryCode: string | null;
  category: string | null;
  productName: string | null;
  description: string | null;
  unitSize: string | null;
  estimatedQuantity: number | null;
  quantity: number | null;
  supplierId: string | null;
  unitPrice: number | null;
  tenderId: string;
};

export type Tender = {
  id: string;
  externalCode: string;
  name: string | null;
  description: string | null;
  statusCode: number;
  procurementType: number | null;
  type: TenderType;
  callType: number | null;
  currency: string | null;
  stages: number | null;
  stagesStatus: number | null;
  claims: number | null;
  creationDate: string;
  closeDate: string;
  initialDate: string;
  finalDate: string;
  publicationDate: string | null;
  signatureDate: string | null;
  adjudicationDate: string;
  amountVisible: number | null;
  estimatedAmount: number | null;
  time: number | null;
  timeUnit: number | null;
  paymentType: number | null;
  paymentOfficer: string | null;
  contactOfficer: string | null;
  contractDuration: number | null;
  contractDurationUnit: number | null;
  adjudicationNumber: string | null;
  url: string | null;
  bidders: number | null;
  buyerId: string;
  items: TenderItem[];
};

// Tender types enum
export enum TenderType {
  L1 = 'L1',
  LE = 'LE',
  LP = 'LP',
  LQ = 'LQ',
  LR = 'LR',
  E2 = 'E2',
  CO = 'CO',
  B2 = 'B2',
  H2 = 'H2',
  I2 = 'I2',
  LS = 'LS',
  O1 = 'O1',
  O2 = 'O2',
  DC = 'DC',
}

export interface CreditLine {
  id: number;
  status: string;
  amount: number;
  productId: number;
  businessIdentifier: string;
  createdAt: string;
  updatedAt: string;
  validUntil?: string | null;
  userOwnerId?: number | null;
  executiveId?: number | null;
  creatorId?: number | null;
  comiteeId?: number | null;
  comitee?: CompanyComitee;
  comiteeOperationsId?: number | null;
  contractDueDate?: string | null;
  observations?: string | null;
  subProductCreditLineConcentrations?: SubProductCreditLineConcentration[];
  concentrations?: CreditLineConcentration[];
}

export interface ConfirmingRequest {
  id: number;
  companyId: number;
  status: string;
  type: string;
  amount: number;
  exception: boolean;
  operationDate: string;
  financingAt: string | null;
  subProductId: string;
  stakeholderIdentifier: string;
  createdById: number;
  lastUserId: number;
  userOwnerId: number | null;
  recipientId: number | null;
  advance: number;
  rate: number;
  operationCostFixed: number;
  operationCostVariable: number;
  operationCostVariablePercentage: number;
  operationCostComissionFixed: number;
  operationCostComissionVariable: number;
  operationCostComissionVariablePercentage: number;
  expensesCost: number | null;
  expensesHasIva: boolean;
  arrearType: string;
  arrearRate: number;
  arrearTableId: number | null;
  comiteeId: number | null;
  comiteeOperationsId: number | null;
  createdAt: string;
  updatedAt: string;
  stakeholderPendingAccountId: number | null;
  exchangeDays: number;

  minutesSinceUpdate: number;
  stakeholderName: string;
  income: number;

  stakeholder?: {
    name: string;
  };
  documents?: ConfirmingRequestDocument[];
}

export interface ConfirmingRequestDocument {
  documentId: string;
  confirmingRequestId: number;
  paymentDate: string;
  active: boolean;
  document: Document;
  businessIdentifier: string;
  businessName: string;
  type: string;
  subType: string;
  amount: number;
  folio: string;
  issuedDate: string;
  daysToCount: number;
}

export interface SubProductCreditLineConcentration {
  id: number;
  creditLineId: number;
  creditLine: CreditLine;
  amount: number;
  amountQualifiedStakeholder?: number;
  rate?: number;
  maxFinancingDays?: number;
  maxDocumentAmount?: number;
  spread?: number;
  arrearRate?: number;
  advance?: number;
  operationCostFixed?: number;
  operationCostVariable?: number;
  discountRateVariable?: number;
  discountRateFixed?: number;
  subProductId: string;
  subProduct: SubProduct;
  createdAt: string;
  updatedAt: string;
}

export interface CreditLineConcentration {
  id: number;
  creditLineId: number;
  creditLine: CreditLine;
  businessIdentifier?: string | null;
  amount: number;
  business?: Business | null;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: number;
  email: string;
  name?: string | null;
  password: string;
  companyId: number;
  company: Company;
  active: boolean;
  pendingPasswordReset: boolean;
  permissionPolicy: number;
}

export interface StatusNode {
  name: string;
  description: string;
  severity?: number;
}

export interface EvaluationNode {
  result: boolean;
  resultText: string;
  nodeName: string;
  nodeIdDecision: number;
  statusNode: StatusNode;
  subDecisionEngineRevisionStory: EvaluationNode[];
}

export interface NodeRange {
  id: number;
  value: number;
  decisionNodeResult: number;
  decisionNodeAssignationId: number;
}

export interface EngineNode {
  id: number;
  originNode: string;
  category: string;
  description: string;
  value: string;
  ranges: NodeRange[];
}

export interface Engine {
  id: number;
  name: string;
  engineType: string;
  revisionObjectiveId: string;
  decisionEngineNodes: EngineNode[];
}

export interface Evaluation {
  id: number;
  label: string;
  targetId: number;
  stakeholderIdentifier?: string;
  statusNode: StatusNode;
  reviewerName: string;
  engine: Engine;
  result: EvaluationNode[];
  createdAt: number;
}

export interface EvaluationsOverview {
  id: number;
  business: { identifier: string; name: string };
  evaluations: Evaluation[];
}

export const PortfolioStatus = {
  DRAFT: 'Borrador',
  WAITING: 'En espera',
  SUCCESS: 'Completado',
  REJECTED_BY_NOTARY: 'Rechazado por notario',
  REJECTED_BY_GOFIRMEX: 'Rechazado por GoFirmex',
  REJECTED_BY_CLIENT: 'Rechazado por cliente',
  REJECTED_BY_EXPIRATION: 'Rechazado por expiración',
  INVALIDATED: 'Invalidado',
} as const;

export const PortfolioStatusKeys = Object.keys(
  PortfolioStatus,
) as (keyof typeof PortfolioStatus)[];

export interface Portfolio {
  id: string;
  title: string;
  type: string;
  providerId?: string | null;
  status: keyof typeof PortfolioStatus;
  requestType?: string | null;
  requestId?: string | null;
  businessIdentifier: string;
  business?: Business;
  signers?: SignerOnPortfolio[];
  documents?: SignDocument[];
  templateId?: string | null;
  template?: { id: string } | null;
  portfolioValidations?: PortfolioValidation[];
  invalidatedAt?: string | null;
  validUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignerOnPortfolio {
  url?: string | null;
  order?: number | null;
  portfolioId: string;
  providerIdentifier: string;
  portfolio?: Portfolio;
}

export enum SignDocumentStatus {
  DRAFT = 'DRAFT',
  WAITING = 'WAITING',
  SUCCESS = 'SUCCESS',
  REJECTED_BY_NOTARY = 'REJECTED_BY_NOTARY',
  REJECTED_BY_GOFIRMEX = 'REJECTED_BY_GOFIRMEX',
  REJECTED_BY_CLIENT = 'REJECTED_BY_CLIENT',
  REJECTED_BY_EXPIRATION = 'REJECTED_BY_EXPIRATION',
}

export enum SignatureType {
  FEA = 'FEA',
  FES = 'FES',
}

export interface SignDocument {
  id: string;
  providerId?: string | null;
  description?: string | null;
  signature: SignatureType;
  status: SignDocumentStatus;
  portfolioId: string;
  portfolio?: Portfolio;
  signers?: SignOnDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface SignOnDocument {
  statusCode: number;
  statusDescription?: string | null;
  providerIdentifier: string;
  documentId: string;
  document?: SignDocument;
  signedAt?: string | null;
  rejectedAt?: string | null;
}

export interface PortfolioTemplate {
  id: string;
  title: string;
  type: string;
  description?: string | null;
  portfolios: Portfolio[];
  portfolioValidations: PortfolioValidation[];
  templateTriggers: TemplateTrigger[];
  signDocumentTemplates: SignDocumentTemplate[];
  defaultSigners: Contact[];
  createdAt: string;
  updatedAt: string;
}

export interface FieldDefinition {
  id: string;
  label: string;
  type: 'text' | 'date' | 'multiselect' | 'operation' | 'select' | 'aval';
  required: boolean;
  placeholder?: string;
  defaultValue?: unknown;
  optionsType?: 'business-users' | 'own-users' | 'operation-types';
  disabled?: boolean;
  helperText?: string;
  order?: number;
  operationType?: string;
  validations?: {
    minLength?: number;
    maxLength?: number;
    regex?: string;
  };
}

export interface SignDocumentTemplate {
  id: string;
  fieldMap?: Record<string, FieldDefinition> | null;
  description?: string | null;
  templateId: string;
  signature: SignatureType;
  template?: PortfolioTemplate;
  extension: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioValidation {
  id: number;
  type: string;
  description?: string | null;
  portfolios: Portfolio[];
  templates: PortfolioTemplate[];
  createdAt: string;
  updatedAt: string;
}

export interface TemplateTrigger {
  id: number;
  type: string;
  description?: string | null;
  templates: PortfolioTemplate[];
  createdAt: string;
  updatedAt: string;
}

export type Role = {
  id: number;
  name: string;
};

export interface PaymentRequestPassive {
  id: number;
  status: string;
  documentsToPay: {
    businessIdentifier: string;
    paymentRequestId: number;
    amount: number;
    document: Document;
  }[];
  documentsApplied: {
    paymentRequestId: number;
    amount: number;
    document: Document;
    paidAt: string;
    createdAt: string;
  }[];
  fundPendingAccounts: object;
  amount: number;
  companyId: number;
  company: object;
  comiteeId: number;
  comitee: object;
  businessIdentifier: string;
  businessName: string;
  createdAt: string;
  updatedAt: string;
  executedAt: string;
}

export interface PendingAccount {
  id: number;
  businessName: string;
  businessIdentifier: string;
  amount: number;
  totalRemaining: number;
  amountToConciliate: number;
  amountAvailable: number;
}

export interface CompanyComitee {
  id: number;
  name: string;
  companyId: number;
  company: Company;
  decisionNodeId?: number | null;
}

export enum PurchaseOrderType {
  OC = 'OC',
  D1 = 'D1',
  C1 = 'C1',
  F3 = 'F3',
  G1 = 'G1',
  R1 = 'R1',
  CA = 'CA',
  SE = 'SE',
  CM = 'CM',
  FG = 'FG',
  TL = 'TL',
  MC = 'MC',
  AG = 'AG',
  CC = 'CC',
  TD = 'TD',
}

export interface PurchaseOrderItem {
  id: string;
  correlativeId: number;
  categoryCode: number;
  category: string;
  productCode: number;
  productName: string;
  buyerSpecification?: string | null;
  supplierSpecification?: string | null;
  quantity?: number | null;
  unit?: string | null;
  currency: Currency;
  netPrice?: number | null;
  totalCharges?: number | null;
  totalDiscounts?: number | null;
  totalTaxes?: number | null;
  total?: number | null;
  purchaseOrderId: string;
}

export interface PurchaseOrder {
  id: string;
  externalCode: string;
  name?: string | null;
  statusCode: number;
  tenderCode?: string | null;
  description?: string | null;
  type: PurchaseOrderType;
  currency: Currency;
  statusCodeSupplier?: number | null;
  creationDate: string;
  shippingDate?: string | null;
  acceptanceDate?: string | null;
  cancellationDate?: string | null;
  lastModificationDate?: string | null;
  qualificationAverage?: number | null;
  qualificationQuantity?: number | null;
  netTotal?: number | null;
  ivaPercentage?: number | null;
  taxes?: number | null;
  total?: number | null;
  shippingType?: string | null;
  paymentType?: string | null;
  buyerId: string;
  buyer: Business;
  supplierId: string;
  supplier: Business;
  items: PurchaseOrderItem[];
  buyerUnitCode?: string | null;
  buyerUnit?: BusinessUnit | null;
  supplierUnitCode?: string | null;
  supplierUnit?: BusinessUnit | null;
  lastScrapedAt?: string | null;
}

enum Currency {
  CLP = 'CLP',
  USD = 'USD',
  UF = 'UF',
}

export interface RequestExpenseCategory {
  id: number;
  name: string;
  hasIva: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DecisionEngine {
  id: number;
  name: string;
  engineType: string;
  revisionObjectiveId: string;
  selected: boolean;
  companyId: number;
  paused: boolean;
  executerNodeId: number | null;
}

export interface DecisionNode {
  id: number;
  name: string;
  description: string;
  companyId?: number;
  severity?: number;
  type: 'boolean' | 'numeric' | 'string' | 'date' | 'final';
  category: string;
  revisionObjective: string;
  operation: string;
  comiteeId: number;
  subDecisionEngineId?: number;
}

export type SocialSecurityArrear = {
  employeeIdentifier: string;
  employeeName: string;
  productType: string;
  institution: string;
  monthInArrear: string;
  letterDate: string;
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  claim: string;
};

export type LaborLawFinesRow = {
  name: string;
  identifier: string;
  origin: string;
  fine: string;
  status: string;
  date: Date;
  amount: string;
  currency: string;
  description: string;
};

export type LaborLawFinesConsult = {
  updatedAt: string | null;
  data: LaborLawFinesRow[];
};

export type InvoicesGeneratedTracker = {
  id: number;
  folio: string;
  docType: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  status: string;
  message?: string;
  issuedDate: string;
  amount: number;
  amountWithIVA: number;
  details: unknown;
  createdAt: string;
  updatedAt: string;
  userId: number;
};
